import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authResendVerification } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, Gift } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "einzelnutzer", label: "Einzelnutzer" },
  { value: "unternehmer", label: "Unternehmer" },
  { value: "steuerberater", label: "Steuerberater" },
];

export default function Register() {
  const { register, registerOrg } = useAuth();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") || "";
  const [accountType, setAccountType] = useState(referralCode ? "einzelnutzer" : "einzelnutzer");
  const [form, setForm] = useState({ email: "", name: "", company: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [resending, setResending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Die Passwörter stimmen nicht überein.");
      return;
    }
    if (accountType === "steuerberater" && !form.company.trim()) {
      toast.error("Bitte geben Sie den Namen Ihrer Kanzlei an.");
      return;
    }
    setBusy(true);
    try {
      const base = {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        company: form.company.trim(),
      };
      const res =
        accountType === "einzelnutzer"
          ? await register({ ...base, ref: referralCode || undefined })
          : await registerOrg({ account_type: accountType, ...base });
      setSentTo(res?.email || form.email.trim());
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Registrierung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await authResendVerification(sentTo);
      toast.success("Bestätigungs-E-Mail wurde erneut gesendet.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte später erneut versuchen.");
    } finally {
      setResending(false);
    }
  };

  if (sentTo) {
    return (
      <AuthShell
        title="Fast geschafft!"
        subtitle="Bitte bestätigen Sie Ihre E-Mail-Adresse."
        footer={
          <span>
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">
              Zur Anmeldung
            </Link>
          </span>
        }
      >
        <div className="space-y-4 text-center" data-testid="register-confirmation">
          <MailCheck className="h-12 w-12 text-primary mx-auto" />
          <p className="text-sm text-foreground">
            Wir haben einen Bestätigungslink an <strong>{sentTo}</strong> gesendet. Bitte öffnen Sie
            die E-Mail und klicken Sie auf <strong>„E-Mail bestätigen"</strong>, um Ihr Konto zu
            aktivieren.
          </p>
          <p className="text-xs text-muted-foreground">
            Der Link ist 12 Stunden gültig. Keine E-Mail erhalten? Bitte auch den Spam-Ordner prüfen.
          </p>
          <Button
            variant="secondary"
            className="w-full border border-border"
            onClick={resend}
            disabled={resending}
            data-testid="resend-verification-button"
          >
            {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : "E-Mail erneut senden"}
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Registrieren"
      subtitle="Erstellen Sie Ihr BeSca-Konto."
      footer={
        <span>
          Bereits registriert?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">
            Zur Anmeldung
          </Link>
        </span>
      }
    >
      {/* Kontotyp-Auswahl */}
      {referralCode && (
        <div
          className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="register-referral-note"
        >
          <Gift className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Sie wurden zu BeSca eingeladen – testen Sie BeSca <strong>kostenlos</strong>.
            Als Einzelnutzer können Sie BeSca später selbst weiterempfehlen und dabei Ihren
            eigenen Einzelplatz dauerhaft gratis sichern.
          </span>
        </div>
      )}
      <div className="mb-4">
        <Label className="mb-2 block">Ich registriere mich als</Label>
        <div className="grid grid-cols-3 gap-1 rounded-md border border-border p-1" data-testid="register-type-selector">
          {TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setAccountType(t.value)}
              data-testid={`register-type-${t.value}`}
              className={`text-sm font-medium py-2 rounded-sm transition-colors ${
                accountType === t.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {accountType === "einzelnutzer" ? (
        <div className="mb-4 border-l-2 border-primary bg-primary/5 px-3 py-2.5 text-sm text-foreground" data-testid="register-promo">
          <span className="font-medium">Der erste Monat ist kostenlos.</span>{" "}
          Angabe der Kreditkarte ist nicht nötig.
        </div>
      ) : (
        <div className="mb-4 border-l-2 border-primary bg-primary/5 px-3 py-2.5 text-sm text-foreground" data-testid="register-promo-org">
          <span className="font-medium">Der erste Monat ist kostenlos.</span>{" "}
          {accountType === "steuerberater"
            ? "Sie können bis zu 3 Mandanten und 2 Ihrer Mitarbeiter einladen, um BeSca vollumfänglich zu testen. Angabe der Kreditkarte ist nicht nötig."
            : "Sie können bis zu 3 Mitarbeiter einladen und BeSca vollumfänglich testen. Angabe der Kreditkarte ist nicht nötig."}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="space-y-2">
          <Label htmlFor="email">E-Mail</Label>
          <Input id="email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" data-testid="register-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input id="name" value={form.name} onChange={set("name")} autoComplete="name" data-testid="register-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">{accountType === "steuerberater" ? "Kanzlei" : "Firma (optional)"}</Label>
          <Input id="company" value={form.company} onChange={set("company")} required={accountType === "steuerberater"} data-testid="register-company" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Passwort</Label>
          <Input id="password" type="password" value={form.password} onChange={set("password")} required autoComplete="new-password" data-testid="register-password" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Passwort bestätigen</Label>
          <Input id="confirm" type="password" value={form.confirm} onChange={set("confirm")} required autoComplete="new-password" data-testid="register-confirm" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konto erstellen"}
        </Button>
      </form>
    </AuthShell>
  );
}
